import { jsx } from 'slate-hyperscript';

import removeNullish from '@/helpers/removeNullish';
import { logError } from '@/providers/ErrorTracking';
import { type SlateElement } from '@/typings/SlateNode';

const deserializeHtmlInternal = (
  el: HTMLElement | ChildNode,
  markAttributes: Record<string, unknown> = {},
): unknown => {
  if (el.nodeType === Node.TEXT_NODE) {
    return jsx('text', markAttributes, el.textContent);
  }
  if (el.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const nodeName = el.nodeName.toLowerCase();
  const htmlElement = el as HTMLElement;

  let childAttributes = { ...markAttributes };

  // Check for inline styles first (for content pasted from Google Docs, Word, etc.)
  if (htmlElement.style) {
    const { fontWeight } = htmlElement.style;
    const { fontStyle } = htmlElement.style;
    const { textDecoration } = htmlElement.style;

    if (fontWeight === 'bold' || fontWeight === '700' || Number.parseInt(fontWeight, 10) >= 700) {
      childAttributes = { ...childAttributes, bold: true };
    }
    if (fontStyle === 'italic') {
      childAttributes = { ...childAttributes, italic: true };
    }
    if (textDecoration && textDecoration.includes('underline')) {
      childAttributes = { ...childAttributes, underline: true };
    }
  }

  // For semantic HTML elements, add their specific formatting
  if (nodeName === 'strong' || nodeName === 'b') {
    childAttributes = { ...childAttributes, bold: true };
  } else if (nodeName === 'em' || nodeName === 'i') {
    childAttributes = { ...childAttributes, italic: true };
  } else if (nodeName === 'u') {
    childAttributes = { ...childAttributes, underline: true };
  } else if (nodeName === 'span') {
    // Span passes through existing attributes (already handled by style check above)
    childAttributes = { ...childAttributes };
  } else if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(nodeName)) {
    // Block elements reset formatting UNLESS they have inline styles
    if (
      !htmlElement.style ||
      (!htmlElement.style.fontWeight && !htmlElement.style.fontStyle && !htmlElement.style.textDecoration)
    ) {
      childAttributes = {};
    }
  } else if (['ol', 'ul', 'li', 'body', 'blockquote'].includes(nodeName)) {
    // List elements and body reset formatting UNLESS they have inline styles
    if (
      !htmlElement.style ||
      (!htmlElement.style.fontWeight && !htmlElement.style.fontStyle && !htmlElement.style.textDecoration)
    ) {
      childAttributes = {};
    }
  } else if (nodeName === 'a') {
    // For 'a' tags, don't pass mark attributes since they create their own element
    childAttributes = {};
  }

  const children = Array.from(el.childNodes)
    .map((node) => deserializeHtmlInternal(node, childAttributes))
    .flat()
    .filter((child) => {
      // Filter out null/undefined
      if (child === null || child === undefined) {
        return false;
      }

      // For block-level containers (body, ul, ol), skip whitespace-only text nodes
      const isBlockContainer = ['body', 'ul', 'ol', 'blockquote'].includes(nodeName);
      if (isBlockContainer && typeof child === 'object' && 'text' in child) {
        const { text } = child;
        if (typeof text === 'string' && !text.trim()) {
          return false;
        }
      }

      return true;
    });

  if (children.length === 0) {
    children.push(jsx('text', {}, ''));
  }

  switch (el.nodeName) {
    case 'BODY':
      return jsx('fragment', {}, children);
    case 'BR':
      return '\n';
    case 'BLOCKQUOTE':
      return jsx('element', { type: 'quote' }, children);
    case 'P':
      return jsx('element', { type: 'paragraph' }, children);
    case 'A':
      return jsx('element', { type: 'link', url: (el as HTMLElement).getAttribute('href') }, children);
    case 'OL':
      return jsx('element', { type: 'numbered-list' }, children);
    case 'UL':
      return jsx('element', { type: 'bulleted-list' }, children);
    case 'LI':
      return jsx('element', { type: 'list-item' }, children);
    default:
      return children;
  }
};

const hasNonWhitespaceText = (node: unknown): boolean => {
  if (!node) {
    return false;
  }

  if (typeof node === 'string') {
    return node.trim().length > 0;
  }

  if (typeof node === 'object' && 'text' in node) {
    return typeof node.text === 'string' && node.text.trim().length > 0;
  }

  if (typeof node === 'object' && 'children' in node && Array.isArray(node.children)) {
    return node.children.some((child) => hasNonWhitespaceText(child));
  }

  return false;
};

const deserializeHtml = (el: HTMLElement | ChildNode, markAttributes: Record<string, unknown> = {}): SlateElement[] => {
  const asSlate = deserializeHtmlInternal(el, markAttributes) as Array<SlateElement>;

  const normalized = asSlate
    .map((node) => {
      if (!node) {
        return node;
      }

      if (typeof node !== 'object') {
        logError('Unsupported node', { asSlate, node });
        return null;
      }

      // Can't have a text node at the top in the SlateEditor.
      // Wrap it in a paragraph instead.
      if ('text' in node) {
        return {
          children: [node],
          type: 'paragraph',
        } as SlateElement;
      }

      if (node.type === 'paragraph' && node.children) {
        const hasContent = hasNonWhitespaceText(node);

        if (!hasContent) {
          return {
            ...node,
            children: [{ text: '' }],
            isEmpty: true,
          };
        }
      }

      return node;
    })
    .filter(removeNullish);

  const deduped: SlateElement[] = [];
  let previousWasEmpty = false;

  normalized.forEach((node) => {
    const { isEmpty } = node as { isEmpty: boolean };

    if (isEmpty) {
      if (!previousWasEmpty) {
        const { isEmpty: _omit, ...cleanNode } = node as SlateElement & { isEmpty: boolean };
        deduped.push(cleanNode as SlateElement);
        previousWasEmpty = true;
      }
    } else {
      deduped.push(node);
      previousWasEmpty = false;
    }
  });

  const grouped: SlateElement[] = [];
  let pendingList: SlateElement[] | null = null;
  let pendingListType: string | null = null;

  const flushList = (): void => {
    if (pendingList && pendingListType) {
      grouped.push({
        children: pendingList,
        type: pendingListType,
      } as SlateElement);
    }
    pendingList = null;
    pendingListType = null;
  };

  deduped.forEach((node) => {
    const { listType } = node as { listType?: string };
    if (node.type === 'list-item' && listType) {
      if (!pendingList) {
        pendingList = [];
        pendingListType = listType;
      } else if (pendingListType !== listType) {
        flushList();
        pendingList = [];
        pendingListType = listType;
      }

      pendingList.push(node);
      return;
    }

    flushList();
    grouped.push(node);
  });
  flushList();

  return grouped;
};

export default deserializeHtml;
